import { dealerAPIController } from '@aqx-core/common/api/dealer/controller';
import { ICompanyExposure, ICompanyExposureParams } from '@aqx-core/common/api/dealer/model';
import {
  DEALER_SUB_KEY_UIDEALER_EXPOSURE,
  DEALER_SUB_KEY_UIDEALER_LATEST_PRICE,
  IDealerExposureData,
  ISymbolTopicLivePrices,
  dealerTopicSubscription,
  socketService,
  symbolTopicSubscription
} from '@aqx-core/common/socket';

import { DEFAULT_SYMBOL_TYPES } from './constants';
import dealerService from './service';
import {
  ECompanyExposureSymbolType,
  EDealerTab,
  EPositionType,
  ICompanyExposurePositionMap,
  ICompanyExposureRowData
} from './types';
import {
  buildSymbolMapFromCompanyExposure,
  calculateNetPosition,
  calculateProfitLoss,
  calculateTotalExposure,
  filterCompanyExposureBySearch,
  mapSymbolToDisplayCompanyExposure,
  transformCompanyExposureFromApi
} from './utils';

export interface IDealerController {
  init: () => Promise<void>;
  fetchCompanyExposure: (params?: ICompanyExposureParams) => Promise<void>;
  setSelectedTab: (tab: EDealerTab) => void;
  searchCompanyExposure: (keyword?: string) => void;
  updateFilteredTypes: (types: ECompanyExposureSymbolType[]) => void;
  toggleTypeFilter: () => void;
  handlePaginationChange: (pageIndex: number, pageSize: number) => void;
  reset: () => void;
  isRequestValid: (requestId: string) => boolean;
  initSocketReconnectionListener: () => void;
  removeSocketReconnectionListener: () => void;
}

class DealerViewController implements IDealerController {
  private currentRequestId: string | null = null;
  private currentSymbolSubscriptionKey = new Set<string>();
  private currentSymbolPriceSubscriptionKey = new Set<string>();

  private get symbolMap(): Map<ICompanyExposure['symbol'], ICompanyExposurePositionMap> {
    return dealerService.context.allSymbolMap;
  }

  private generateRequestId = (): string => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  public isRequestValid = (requestId: string): boolean => {
    return this.currentRequestId === requestId;
  };

  public init = async (): Promise<void> => {
    dealerService.setLoading(true);
    try {
      await this.fetchCompanyExposure({ symbolType: DEFAULT_SYMBOL_TYPES });
    } catch {
    } finally {
      dealerService.setLoading(false);
    }
  };

  public fetchCompanyExposure = async (params?: ICompanyExposureParams): Promise<void> => {
    const requestId = this.generateRequestId();
    this.currentRequestId = requestId;
    this.unsubscribeDealerExposureTopic();

    dealerService.setLoading(true);
    try {
      const defaultParams = {
        ...params
      };

      await dealerAPIController.getCompanyExposure(
        {
          onSuccess: (result?: ICompanyExposure[] | null) => {
            if (!this.isRequestValid(requestId)) {
              return;
            }

            const rawData = result || [];
            const data = transformCompanyExposureFromApi(rawData);
            const symbolMap = buildSymbolMapFromCompanyExposure(data);
            const filteredData = mapSymbolToDisplayCompanyExposure(symbolMap);

            dealerService.setAllCompanyExposureData(filteredData);
            dealerService.setLiveSymbolExposureMap(symbolMap);

            const { pageIndex, pageSize } = dealerService.context.pagination;
            const displayData = this.filterCurrentDisplayData(pageIndex, pageSize);
            this.handleDisplayDataChange(displayData);
          },
          onError: () => {
            if (!this.isRequestValid(requestId)) {
              return;
            }
            dealerService.setAllCompanyExposureData([]);
          }
        },
        defaultParams
      );
    } catch {
      if (this.isRequestValid(requestId)) {
        dealerService.setAllCompanyExposureData([]);
      }
    } finally {
      if (this.isRequestValid(requestId)) {
        dealerService.setLoading(false);
      }
    }
  };

  public setSelectedTab = (tab: EDealerTab): void => {
    dealerService.setSelectedTab(tab);

    if (tab === EDealerTab.COMPANY_EXPOSURE) {
      const currentTypes = dealerService.context.checkedTypes;
      this.fetchCompanyExposure({ symbolType: currentTypes.length > 0 ? currentTypes : DEFAULT_SYMBOL_TYPES });
    }
  };

  public searchCompanyExposure = (keyword?: string): void => {
    const currentData = dealerService.context.allCompanyExposure;
    const searchResult = filterCompanyExposureBySearch(currentData, keyword);

    dealerService.setSearch(keyword);
    dealerService.setCompanyExposureSearchResult(searchResult);

    const { pageIndex, pageSize } = dealerService.context.pagination;
    this.handleDisplayDataChange(this.filterCurrentDisplayData(pageIndex, pageSize));
  };

  public updateFilteredTypes = (types: ECompanyExposureSymbolType[]): void => {
    dealerService.setCheckedTypes(types);

    if (types.length === 0) {
      dealerService.setAllCompanyExposureData([]);
      dealerService.setLoading(false);
    } else {
      this.fetchCompanyExposure({ symbolType: types });
    }
  };

  public refetchCompanyExposure = async () => {
    dealerService.setLoading(true);
    try {
      const currentTypes = dealerService.context.checkedTypes;
      if (currentTypes.length === 0) {
        return;
      }

      await this.fetchCompanyExposure({ symbolType: currentTypes });
    } catch {
    } finally {
      dealerService.setLoading(false);
    }
  };

  public toggleTypeFilter = (): void => {
    dealerService.toggleShowTypeFilter();
  };

  public handlePaginationChange = (pageIndex: number, pageSize: number): void => {
    dealerService.setPagination(pageIndex, pageSize);

    const displayData = this.filterCurrentDisplayData(pageIndex, pageSize);
    this.handleDisplayDataChange(displayData);
  };

  public reset = (): void => {
    // Unsubscribe from all current subscriptions
    this.unsubscribeDealerExposureTopic();
    this.unsubscribeSymbolTopic();
    dealerService.reset();
  };

  // Sockets
  public initSocketReconnectionListener = (): void => {
    socketService.addReconnectListener(this.onReconnectSocket);
  };

  public removeSocketReconnectionListener = (): void => {
    socketService.removeReconnectListener(this.onReconnectSocket);
  };

  private subscribeDealerExposureTopic = (): void => {
    const displayData = dealerService.context.displayCompanyExposure;

    displayData.forEach((item) => {
      const symbolToSubscribe = item.symbol;
      if (symbolToSubscribe && !this.currentSymbolSubscriptionKey.has(symbolToSubscribe)) {
        dealerTopicSubscription.subscribe(
          symbolToSubscribe,
          DEALER_SUB_KEY_UIDEALER_EXPOSURE,
          this.updateDealerExposure
        );
        this.currentSymbolSubscriptionKey.add(symbolToSubscribe);
      }
    });
  };

  private unsubscribeDealerExposureTopic = (): void => {
    this.currentSymbolSubscriptionKey.forEach((subscribedSymbol) => {
      dealerTopicSubscription.unsubscribe(
        subscribedSymbol,
        DEALER_SUB_KEY_UIDEALER_EXPOSURE,
        this.updateDealerExposure
      );
    });
    this.currentSymbolSubscriptionKey.clear();
  };

  private subscribeSymbolTopic = () => {
    const displayData = dealerService.context.displayCompanyExposure;
    displayData.forEach((item) => {
      const symbolToSubscribe = item.symbol;
      if (symbolToSubscribe && !this.currentSymbolPriceSubscriptionKey.has(symbolToSubscribe)) {
        symbolTopicSubscription.subscribe(
          symbolToSubscribe,
          DEALER_SUB_KEY_UIDEALER_LATEST_PRICE,
          this.updateDealerExposureLivePrice
        );
        this.currentSymbolPriceSubscriptionKey.add(symbolToSubscribe);
      }
    });
  };

  private unsubscribeSymbolTopic = (): void => {
    this.currentSymbolPriceSubscriptionKey.forEach((subscribedSymbol) => {
      symbolTopicSubscription.unsubscribe(
        subscribedSymbol,
        DEALER_SUB_KEY_UIDEALER_LATEST_PRICE,
        this.updateDealerExposureLivePrice
      );
    });
    this.currentSymbolPriceSubscriptionKey.clear();
  };

  private onReconnectSocket = (): void => {
    this.refetchCompanyExposure();
  };

  private updateDealerExposure = (dealerExposure: IDealerExposureData): void => {
    let hasUpdated = false;
    const updateExposure = this.symbolMap.get(dealerExposure.symbol);
    if (!updateExposure) {
      return;
    }

    if (updateExposure.buyPosition && dealerExposure.type === EPositionType.BUY) {
      updateExposure.buyPosition = this.updatePositionFromDealerExposure(updateExposure.buyPosition, dealerExposure);
      hasUpdated = true;
    }

    if (updateExposure.sellPosition && dealerExposure.type === EPositionType.SELL) {
      updateExposure.sellPosition = this.updatePositionFromDealerExposure(updateExposure.sellPosition, dealerExposure);
      hasUpdated = true;
    }

    if (updateExposure.buyPosition && updateExposure.sellPosition) {
      const newNetPosition = calculateNetPosition(updateExposure.buyPosition, updateExposure.sellPosition);
      updateExposure.netPosition = newNetPosition;
      hasUpdated = true;
    }

    if (hasUpdated) {
      dealerService.setLiveSymbolExposureMap(this.symbolMap);
    }
  };

  private updateDealerExposureLivePrice = (updatedSymbolId: string, livePrices: ISymbolTopicLivePrices) => {
    let hasUpdated = false;
    const updateSymbol = this.symbolMap.get(updatedSymbolId);
    if (!updateSymbol) {
      return;
    }

    if (updateSymbol.buyPosition) {
      updateSymbol.buyPosition = this.updatePositionLatestPrice(updateSymbol.buyPosition, livePrices);
      updateSymbol.buyPosition = this.updatePositionProfit(updateSymbol.buyPosition, livePrices);
      updateSymbol.buyPosition = this.updatePositionTotalExposure(updateSymbol.buyPosition, livePrices);
      hasUpdated = true;
    }

    if (updateSymbol.sellPosition) {
      updateSymbol.sellPosition = this.updatePositionLatestPrice(updateSymbol.sellPosition, livePrices);
      updateSymbol.sellPosition = this.updatePositionProfit(updateSymbol.sellPosition, livePrices);
      updateSymbol.sellPosition = this.updatePositionTotalExposure(updateSymbol.sellPosition, livePrices);
      hasUpdated = true;
    }

    if (updateSymbol.netPosition) {
      updateSymbol.netPosition = this.updatePositionLatestPrice(updateSymbol.netPosition, livePrices);
      if (updateSymbol.buyPosition && updateSymbol.sellPosition) {
        updateSymbol.netPosition = calculateNetPosition(updateSymbol.buyPosition, updateSymbol.sellPosition);
      }
      hasUpdated = true;
    }

    if (hasUpdated) {
      dealerService.setLiveSymbolExposureMap(this.symbolMap);
    }
  };

  private updatePositionFromDealerExposure = (
    position: ICompanyExposure,
    dealerExposure: IDealerExposureData
  ): ICompanyExposure => {
    return {
      ...position,
      lotSize: dealerExposure.lotSize,
      volume: dealerExposure.volume,
      units: dealerExposure.units,
      openPrice: dealerExposure.entryPrice,
      latestTradeTime: dealerExposure.latestTradeTime
    };
  };

  private updatePositionLatestPrice = (
    position: ICompanyExposure,
    livePrices: ISymbolTopicLivePrices
  ): ICompanyExposure => {
    const latestPrice = this.getLatestPriceByPositionType(position.positionType, livePrices);
    return {
      ...position,
      latestPrice
    };
  };

  private updatePositionProfit = (position: ICompanyExposure, livePrices: ISymbolTopicLivePrices): ICompanyExposure => {
    const { openPrice, lotSize, positionType, exchangeRate } = position;
    const latestPrice = this.getLatestPriceByPositionType(positionType, livePrices);
    const profit = calculateProfitLoss(positionType, latestPrice, openPrice, lotSize, exchangeRate);

    return {
      ...position,
      profit
    };
  };

  private updatePositionTotalExposure = (
    position: ICompanyExposure,
    livePrices: ISymbolTopicLivePrices
  ): ICompanyExposure => {
    const { units, exchangeRate } = position;
    const currentPrice = this.getLatestPriceByPositionType(position.positionType, livePrices);
    const totalExposure = calculateTotalExposure(currentPrice, units, exchangeRate);

    return {
      ...position,
      totalExposure
    };
  };

  private getLatestPriceByPositionType = (positionType: EPositionType, livePrices: ISymbolTopicLivePrices): number => {
    return positionType === EPositionType.BUY ? livePrices.liveBuy : livePrices.liveSell;
  };

  private filterCurrentDisplayData = (pageIndex: number, pageSize: number): ICompanyExposureRowData[] => {
    const currentData = dealerService.context.companyExposureSearchResult;
    return currentData.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);
  };

  private handleDisplayDataChange = (displayData: ICompanyExposureRowData[]): void => {
    this.unsubscribeDealerExposureTopic();
    this.unsubscribeSymbolTopic();

    dealerService.setDisplayCompanyExposure(displayData);

    this.subscribeDealerExposureTopic();
    this.subscribeSymbolTopic();
  };
}

export const dealerViewController = new DealerViewController();
